import userService from './userService';
import permessiService from './permessiService';
import { vediCome } from './vediCome';

/**
 * Lo spazio attivo: quale spazio di lavoro (squadra + strutture) stiamo guardando.
 *
 * Il valore viaggia verso il server nell'intestazione X-Spazio, aggiunta dall'interceptor
 * di richiesta in api.js. Se l'intestazione manca il server ripiega sull'ultimo spazio
 * usato: tenerne una copia qui serve a non far "saltare" lo spazio fra una scheda e
 * l'altra del browser, non e' un requisito del server.
 *
 * PERCHE' AL CAMBIO SI RICARICA LA PAGINA
 * Nel progetto ci sono oltre quaranta viste che si tengono i dati dello spazio dentro
 * data() e li caricano una volta sola in mounted(): prenotazioni, strutture, messaggi,
 * report, dispositivi... Cambiare spazio senza ricaricare vorrebbe dire insegnare a
 * ognuna di quelle viste ad accorgersi del cambio, e finche' non lo facessero tutte
 * resterebbero a schermo dati dello spazio precedente — cioe' dati di qualcun altro,
 * che e' il tipo di sbaglio che non ci si puo' permettere qui. La ricarica e' brutale ma
 * non lascia niente di vecchio in memoria, e si cambia spazio poche volte al giorno.
 */

// Chiave dedicata, nello stile delle altre di Zoelia (cfr. 'zoelia_tokens' in keycloak.js).
const CHIAVE_SPAZIO = 'zoelia_spazio_attivo';

// Rientro alla home dopo il cambio: la pagina corrente potrebbe essere il dettaglio di una
// prenotazione o di una struttura dello spazio vecchio, che nel nuovo non esiste.
// E' solo il ripiego di quando non si conosce il ruolo: la home e' il cruscotto, e il cruscotto
// non e' area di tutti (governante e proprietario ci troverebbero un 403 per ogni chiamata).
const DOPO_IL_CAMBIO = '/';

/** Dove atterrare entrando in uno spazio con quel ruolo. Ruolo ignoto: si ripiega sulla home. */
function destinazionePerRuolo(ruolo) {
  return ruolo ? permessiService.paginaIniziale(ruolo) : DOPO_IL_CAMBIO;
}

function leggiDaDisco() {
  try {
    return vediCome.deposito().getItem(CHIAVE_SPAZIO) || null;
  } catch (e) {
    // Navigazione privata o storage negato: si lavora lo stesso, decide il server.
    return null;
  }
}

function scriviSuDisco(id) {
  try {
    if (id) {
      vediCome.deposito().setItem(CHIAVE_SPAZIO, id);
    } else {
      vediCome.deposito().removeItem(CHIAVE_SPAZIO);
    }
  } catch (e) {
    console.warn('Impossibile salvare lo spazio attivo:', e);
  }
}

let spazioAttivo = leggiDaDisco();

// Un solo giro di ripartenza: se piu' richieste in volo tornano insieme con
// SPAZIO_NON_CONSENTITO non devono innescare N ricariche una sull'altra.
let ripartenzaInCorso = false;

export const spazioService = {
  /** Identificativo dello spazio attivo, o null se non ne abbiamo ancora uno. */
  getAttivo() {
    return spazioAttivo;
  },

  /**
   * Allinea la copia locale a quella che il server ha appena dichiarato in /account/me.
   * Non ricarica niente: qui non stiamo cambiando spazio, stiamo solo prendendo atto.
   */
  allinea(id) {
    const nuovo = id || null;
    if (nuovo === spazioAttivo) return;
    spazioAttivo = nuovo;
    scriviSuDisco(nuovo);
  },

  /**
   * Cambia spazio davvero: butta via il profilo in cache (che porta con se' ruolo,
   * strutture e feature del vecchio spazio) e riparte con la pagina ricaricata.
   *
   * La destinazione dipende dal ruolo che si ha NEL NUOVO spazio, non da quello di prima:
   * chi entra come governante o come proprietario sul cruscotto non ci puo' stare, e ci
   * troverebbe una pagina vuota piena di errori rossi a ogni cambio.
   *
   * @param {string} id identificativo dello spazio in cui entrare.
   * @param {string} [ruolo] il ruolo che si ha in quello spazio (l'oggetto spazio ha .ruolo).
   *                 Facoltativo: se non lo si passa si ripiega sulla home, come prima.
   * @param {string} [destinazione] dove atterrare nel nuovo spazio, se lo si sa gia'. Serve al
   *                 tocco su una notifica push di un altro spazio: la pagina a cui porta (una
   *                 conversazione, un check-in) e' del NUOVO spazio, quindi la ragione per cui di
   *                 solito si riparte dalla pagina iniziale — i dati dello spazio vecchio — qui non
   *                 vale. Senza, si fa come sempre: il sito la chiama con due argomenti.
   * @returns {boolean} false se lo spazio chiesto era gia' quello attivo.
   */
  cambia(id, ruolo, destinazione) {
    if (!id || id === spazioAttivo) return false;

    // Esiste da sempre e non la chiamava nessuno: il profilo restava quello del vecchio
    // spazio anche dopo il cambio, feature e ruolo compresi.
    userService.clearCache();

    spazioAttivo = id;
    scriviSuDisco(id);
    window.location.assign(destinazione || destinazionePerRuolo(ruolo));
    return true;
  },

  /** Dimentica lo spazio salvato senza ricaricare (serve al logout). */
  dimentica() {
    spazioAttivo = null;
    scriviSuDisco(null);
  },

  /**
   * Lo spazio salvato non e' piu' nostro (invito revocato, membro rimosso): lo si dimentica
   * e si riparte da capo. Senza intestazione X-Spazio il server ci rimette nell'ultimo
   * spazio legittimo, quindi la ripartenza non puo' avvitarsi su se stessa.
   */
  ripartiSenzaSpazio() {
    if (ripartenzaInCorso) return;
    ripartenzaInCorso = true;
    console.warn("Spazio non piu' consentito: si riparte senza spazio salvato.");
    userService.clearCache();
    this.dimentica();
    window.location.assign(DOPO_IL_CAMBIO);
  },
};

export default spazioService;
